import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import UserHeader from '../components/UserHeader';
import MultiRouteMap from '../components/MultiRouteMap';
import LoadingSpinner from '../components/LoadingSpinner';
import { apiRequest, UPLOADS_BASE_URL } from '../config/api';
import { getAvatarStyle, getInitials } from '../utils/avatarUtils';

interface Aircraft {
  _id: string;
  identification: string;
  type: string;
}

interface Pilot {
  name: string;
  organization?: string;
  logo?: string;
}

interface PublicFlight {
  _id: string;
  userid: string;
  aircraftid: Aircraft | null;
  dateStart: string;
  duration: number;
  region?: string;
  coordinates?: { latitude: number; longitude: number; altitude: number; timestamp: string }[];
  pilot: Pilot | null;
}

const AIRCRAFT_TYPE_LABELS: Record<string, string> = {
  balloon: 'Ballon',       ballon: 'Ballon',
  glider: 'Segelflugzeug', segelflugzeug: 'Segelflugzeug',
  plane: 'Motorflugzeug',  motorflugzeug: 'Motorflugzeug',
  helicopter: 'Hubschrauber', hubschrauber: 'Hubschrauber',
};

const AllFlights: React.FC = () => {
  const navigate = useNavigate();

  const [flights, setFlights] = useState<PublicFlight[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const getInitialState = (key: string, defaultValue: any) => {
    const saved = sessionStorage.getItem(`all_flights_${key}`);
    try { return saved !== null ? JSON.parse(saved) : defaultValue; }
    catch { return defaultValue; }
  };

  // Filters
  const [dateRange, setDateRange] = useState(() => getInitialState('dateRange', 'Letzte 30 Tage'));
  const [showCustomDate, setShowCustomDate] = useState(() => getInitialState('showCustomDate', false));
  const [startDate, setStartDate] = useState(() => getInitialState('startDate', ''));
  const [endDate, setEndDate] = useState(() => getInitialState('endDate', ''));
  const [aircraftType, setAircraftType] = useState(() => getInitialState('aircraftType', 'Alle Typen'));
  const [region, setRegion] = useState(() => getInitialState('region', ''));

  useEffect(() => {
    sessionStorage.setItem('all_flights_dateRange', JSON.stringify(dateRange));
    sessionStorage.setItem('all_flights_showCustomDate', JSON.stringify(showCustomDate));
    sessionStorage.setItem('all_flights_startDate', JSON.stringify(startDate));
    sessionStorage.setItem('all_flights_endDate', JSON.stringify(endDate));
    sessionStorage.setItem('all_flights_aircraftType', JSON.stringify(aircraftType));
    sessionStorage.setItem('all_flights_region', JSON.stringify(region));
  }, [dateRange, showCustomDate, startDate, endDate, aircraftType, region]);

  const calculateDateRange = (range: string): { from: string | null; to: string | null } => {
    const now = new Date();
    const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    switch (range) {
      case 'Letzte 7 Tage': {
        const d = new Date(now); d.setDate(d.getDate() - 7);
        return { from: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`, to: todayStr };
      }
      case 'Letzte 30 Tage': {
        const d = new Date(now); d.setDate(d.getDate() - 30);
        return { from: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`, to: todayStr };
      }
      case 'Letzte 90 Tage': {
        const d = new Date(now); d.setDate(d.getDate() - 90);
        return { from: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`, to: todayStr };
      }
      case 'Dieses Jahr':
        return { from: `${now.getFullYear()}-01-01`, to: todayStr };
      default:
        return { from: null, to: null };
    }
  };

  const mapAircraftTypeToApi = (label: string): string => {
    const m: Record<string, string> = {
      'Alle Typen': 'all', 'Ballon': 'balloon', 'Segelflugzeug': 'glider',
      'Motorflugzeug': 'plane', 'Hubschrauber': 'helicopter',
    };
    return m[label] || 'all';
  };

  const fetchFlights = async (options: {
    dateFrom: string | null; dateTo: string | null; aircraftTypeApi: string;
  }) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (options.dateFrom) params.append('dateFrom', options.dateFrom);
      if (options.dateTo) params.append('dateTo', options.dateTo);
      if (options.aircraftTypeApi !== 'all') params.append('aircraftType', options.aircraftTypeApi);
      params.append('includeCoordinates', 'true');
      const qs = params.toString();
      const response = await apiRequest(`/flight-tracks/public${qs ? `?${qs}` : ''}`);
      if (!response.ok) throw new Error('Fehler beim Laden');
      const data: PublicFlight[] = await response.json();
      setFlights(data.filter(f => f.aircraftid));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  };

  // Initial fetch
  useEffect(() => {
    const isCustom = dateRange.includes(' - ') || dateRange.includes(' – ');
    const dates = isCustom
      ? { from: startDate || null, to: endDate || null }
      : calculateDateRange(dateRange);
    fetchFlights({ dateFrom: dates.from, dateTo: dates.to, aircraftTypeApi: mapAircraftTypeToApi(aircraftType) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyFilters = (overrides?: { dateFrom?: string | null; dateTo?: string | null; aircraftTypeApi?: string }) => {
    const isCustom = dateRange.includes(' - ') || dateRange.includes(' – ');
    const dates = isCustom
      ? { from: startDate || null, to: endDate || null }
      : calculateDateRange(dateRange);
    fetchFlights({
      dateFrom: overrides?.dateFrom !== undefined ? overrides.dateFrom : dates.from,
      dateTo: overrides?.dateTo !== undefined ? overrides.dateTo : dates.to,
      aircraftTypeApi: overrides?.aircraftTypeApi !== undefined ? overrides.aircraftTypeApi : mapAircraftTypeToApi(aircraftType),
    });
  };

  const handleDateRangeSelect = (range: string) => {
    if (range === 'Benutzerdefiniert') {
      setShowCustomDate(true);
    } else {
      setShowCustomDate(false);
      setDateRange(range);
      const dates = calculateDateRange(range);
      fetchFlights({ dateFrom: dates.from, dateTo: dates.to, aircraftTypeApi: mapAircraftTypeToApi(aircraftType) });
    }
  };

  const applyCustomDateRange = () => {
    if (startDate && endDate) {
      setDateRange(`${startDate} – ${endDate}`);
      setShowCustomDate(false);
      fetchFlights({ dateFrom: startDate, dateTo: endDate, aircraftTypeApi: mapAircraftTypeToApi(aircraftType) });
    }
  };

  const handleAircraftTypeSelect = (label: string) => {
    setAircraftType(label);
    const isCustom = dateRange.includes(' - ') || dateRange.includes(' – ');
    const dates = isCustom
      ? { from: startDate || null, to: endDate || null }
      : calculateDateRange(dateRange);
    fetchFlights({ dateFrom: dates.from, dateTo: dates.to, aircraftTypeApi: mapAircraftTypeToApi(label) });
  };

  const formatDate = (s: string) =>
    new Date(s).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });

  const formatDuration = (secs: number | null | undefined) => {
    if (secs == null || isNaN(secs)) return '(Nicht beendet)';
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60);
    return `${h}:${m.toString().padStart(2, '0')} h`;
  };

  // Client-side region filter
  const visibleFlights = region.trim()
    ? flights.filter(f => f.region?.toLowerCase().includes(region.trim().toLowerCase()))
    : flights;

  return (
    <div className="container" style={{ padding: '40px' }}>
      <UserHeader title="Alle Flüge" />

      {/* Filters */}
      <div className="card shadow-sm mb-4" style={{ borderRadius: '12px' }}>
        <div className="card-body p-4">
          <div className="d-flex flex-wrap gap-3 align-items-center">
            {/* Date range */}
            <span style={{ color: '#687088', fontSize: '14px' }}>Zeitraum</span>
            <div className="dropdown">
              <button
                className="btn btn-primary dropdown-toggle"
                style={{ fontWeight: 700, fontSize: '13px' }}
                type="button"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                {dateRange}
              </button>
              <ul
                className="dropdown-menu"
                style={{ minWidth: showCustomDate ? '320px' : 'auto', padding: showCustomDate ? '16px' : '' }}
                onClick={(e) => { if (showCustomDate) e.stopPropagation(); }}
              >
                {!showCustomDate ? (
                  <>
                    {['Letzte 7 Tage', 'Letzte 30 Tage', 'Letzte 90 Tage', 'Dieses Jahr'].map(r => (
                      <li key={r}>
                        <a className={`dropdown-item ${dateRange === r ? 'active' : ''}`} href="#"
                          onClick={(e) => { e.preventDefault(); handleDateRangeSelect(r); }}>
                          {r}
                        </a>
                      </li>
                    ))}
                    <li><hr className="dropdown-divider" /></li>
                    <li>
                      <a className="dropdown-item" href="#"
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleDateRangeSelect('Benutzerdefiniert'); }}>
                        Benutzerdefiniert...
                      </a>
                    </li>
                  </>
                ) : (
                  <div onClick={(e) => e.stopPropagation()}>
                    <div className="mb-3">
                      <label className="form-label" style={{ fontSize: '13px', color: '#687088', fontWeight: 600 }}>Von</label>
                      <input type="date" className="form-control" value={startDate}
                        onChange={(e) => setStartDate(e.target.value)} style={{ fontSize: '13px' }} />
                    </div>
                    <div className="mb-3">
                      <label className="form-label" style={{ fontSize: '13px', color: '#687088', fontWeight: 600 }}>Bis</label>
                      <input type="date" className="form-control" value={endDate}
                        onChange={(e) => setEndDate(e.target.value)} style={{ fontSize: '13px' }} />
                    </div>
                    <div className="d-flex gap-2">
                      <button className="btn btn-sm btn-outline-secondary" style={{ fontSize: '13px', flex: 1 }}
                        onClick={() => { setShowCustomDate(false); setStartDate(''); setEndDate(''); }}>
                        Abbrechen
                      </button>
                      <button className="btn btn-sm btn-primary" style={{ fontSize: '13px', fontWeight: 700, flex: 1 }}
                        onClick={applyCustomDateRange} disabled={!startDate || !endDate}>
                        Anwenden
                      </button>
                    </div>
                  </div>
                )}
              </ul>
            </div>

            {/* Aircraft type */}
            <span style={{ color: '#687088', fontSize: '14px' }}>Flugobjekt</span>
            <div className="dropdown">
              <button
                className="btn btn-outline-secondary dropdown-toggle"
                style={{ color: '#687088', borderColor: '#dee2e6', fontSize: '13px' }}
                type="button"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                {aircraftType}
              </button>
              <ul className="dropdown-menu">
                {['Alle Typen', 'Ballon', 'Segelflugzeug', 'Motorflugzeug', 'Hubschrauber'].map(t => (
                  <li key={t}>
                    <a className={`dropdown-item ${aircraftType === t ? 'active' : ''}`} href="#"
                      onClick={(e) => { e.preventDefault(); handleAircraftTypeSelect(t); }}>
                      {t}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            {/* Region */}
            <span style={{ color: '#687088', fontSize: '14px' }}>Region</span>
            <input
              type="text"
              className="form-control"
              placeholder="Alle Regionen"
              value={region}
              onChange={(e) => setRegion(e.target.value)}
              style={{ fontSize: '13px', width: '160px' }}
            />
            
            <button
              className="btn btn-outline-secondary ms-auto"
              style={{ fontSize: '13px' }}
              onClick={() => {
                setDateRange('Letzte 30 Tage');
                setAircraftType('Alle Typen');
                setRegion('');
                setShowCustomDate(false);
                setStartDate('');
                setEndDate('');
                
                // Immediately apply default filters
                const dates = calculateDateRange('Letzte 30 Tage');
                fetchFlights({
                  dateFrom: dates.from,
                  dateTo: dates.to,
                  aircraftTypeApi: 'all'
                });
              }}
            >
              Zurücksetzen
            </button>
          </div>
        </div>
      </div>

      {/* Map */}
      <div className="card shadow-sm mb-4" style={{ borderRadius: '12px' }}>
        <div className="card-body p-4">
          <h5 style={{ fontWeight: 700, color: '#687088', marginBottom: '16px' }}>Karte</h5>
          {loading ? (
            <LoadingSpinner message="Lade Flüge..." />
          ) : (
            <MultiRouteMap
              flights={visibleFlights}
              selectedId={selectedId}
              onFlightSelect={(id) => setSelectedId(id === selectedId ? null : id)}
              height="600px"
            />
          )}
        </div>
      </div>

      {/* Flight list */}
      <div className="card shadow-sm" style={{ borderRadius: '12px' }}>
        <div className="card-body p-4">
          <h5 style={{ fontWeight: 700, color: '#687088', marginBottom: '16px' }}>
            Flüge{visibleFlights.length > 0 ? ` (${visibleFlights.length})` : ''}
          </h5>
          {loading ? (
            <LoadingSpinner message="Lade Flüge..." />
          ) : error ? (
            <p style={{ color: '#dc3545' }}>{error}</p>
          ) : visibleFlights.length === 0 ? (
            <p style={{ color: '#a0afc0', fontSize: '14px' }}>Keine öffentlichen Flüge gefunden.</p>
          ) : (
            <div className="table-responsive">
              <table className="table" style={{ color: '#687088' }}>
                <thead>
                  <tr>
                    <th>Datum</th>
                    <th>Gerät</th>
                    <th>Kennung</th>
                    <th>Dauer</th>
                    <th>Region</th>
                    <th>Pilot</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {visibleFlights.map(f => {
                    const isSelected = f._id === selectedId;
                    return (
                      <tr
                        key={f._id}
                        onClick={() => setSelectedId(f._id === selectedId ? null : f._id)}
                        style={{
                          cursor: 'pointer',
                          background: isSelected ? '#f0f7ff' : undefined,
                        }}
                      >
                        <td>{formatDate(f.dateStart)}</td>
                        <td>{AIRCRAFT_TYPE_LABELS[f.aircraftid?.type || ''] || f.aircraftid?.type || '-'}</td>
                        <td style={{ fontWeight: 600 }}>{f.aircraftid?.identification || '-'}</td>
                        <td>{formatDuration(f.duration)}</td>
                        <td>{f.region || '-'}</td>
                        <td>
                          {f.pilot ? (
                            <div className="d-flex align-items-center gap-2">
                              {f.pilot.logo ? (
                                <img
                                  src={`${UPLOADS_BASE_URL}${f.pilot.logo}`}
                                  alt={f.pilot.name}
                                  style={{ width: '28px', height: '28px', borderRadius: '50%', objectFit: 'cover' }}
                                />
                              ) : (
                                <div style={{ ...getAvatarStyle(f.pilot.name, 28), fontSize: '11px' }}>
                                  {getInitials(f.pilot.name)}
                                </div>
                              )}
                              <span style={{ fontSize: '13px' }}>
                                {f.pilot.name}
                                {f.pilot.organization && (
                                  <span style={{ color: '#a0afc0' }}> · {f.pilot.organization}</span>
                                )}
                              </span>
                            </div>
                          ) : (
                            <span style={{ color: '#a0afc0', fontSize: '13px' }}>–</span>
                          )}
                        </td>
                        <td>
                          <Link
                            to={`/all-flights/${f._id}`}
                            style={{ color: '#0d6efd', textDecoration: 'none', fontSize: '13px', fontWeight: 600 }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            Details
                          </Link>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AllFlights;
